import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import BaseUrl from "../../../constants/BaseUrl";
import { useSelector } from "react-redux";
import Breadcrumbs from "../../reusebale/breadcrumbs";
import TransactionsList from "./transactionsList";

const TransactionsTable = ({ onBack, leaseId, propertyId, landlordId }) => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [leaseDetails, setLeaseDetails] = useState(null);
  const [leaseMap, setLeaseMap] = useState({}); // Map leaseId to lease details for property transactions
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 0,
    limit: 10,
  });

  const itemsPerPage = 10;

  const handlePageClick = (selectedPage) => {
    const newPage = selectedPage.selected + 1; // Convert 0-indexed to 1-indexed
    if (leaseId) {
      fetchLeaseTransactions(newPage);
    } else if (propertyId) {
      fetchPropertyTransactions(newPage);
    } else if (landlordId) {
      fetchLandlordTransactions(newPage);
    }
  };

  const fetchLeaseTransactions = async (page = 1) => {
    if (!leaseId) return;
    
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    try {
      // Fetch transactions with pagination
      const transactionsResponse = await fetch(
        `${BaseUrl}api/payment-transaction/lease/${leaseId}?page=${page}&limit=${itemsPerPage}`,
        requestOptions
      );
      const transactionsResult = await transactionsResponse.json();

      if (transactionsResult.status === "success" && transactionsResult.data) {
        setTransactions(transactionsResult.data);
        if (transactionsResult.pagination) {
          setPagination(transactionsResult.pagination);
        }
      } else {
        setTransactions([]);
        setPagination({
          total: 0,
          currentPage: 1,
          totalPages: 0,
          limit: itemsPerPage,
        });
      }

      // Fetch lease details to get tenant and property info (only on first load)
      if (page === 1 && !leaseDetails) {
        const leaseResponse = await fetch(
          `${BaseUrl}api/lease/getLeaseDetails/${leaseId}`,
          requestOptions
        );
        const leaseResult = await leaseResponse.json();

        if (leaseResult.success) {
          setLeaseDetails(leaseResult.data);
        }
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
      setTransactions([]);
      setPagination({
        total: 0,
        currentPage: 1,
        totalPages: 0,
        limit: itemsPerPage,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyTransactions = async (page = 1) => {
    if (!propertyId) return;

    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    try {
      const transactionsResponse = await fetch(
        `${BaseUrl}api/payment-transaction/property/${propertyId}?page=${page}&limit=${itemsPerPage}`,
        requestOptions
      );
      const transactionsResult = await transactionsResponse.json();

      if (transactionsResult.status === "success" && transactionsResult.data) {
        setTransactions(transactionsResult.data);
        if (transactionsResult.pagination) {
          setPagination(transactionsResult.pagination);
        }

        // Get unique lease IDs from transactions
        const uniqueLeaseIds = [...new Set(transactionsResult.data.map(t => t.leaseId).filter(Boolean))];

        // Fetch lease details for tenant info
        if (uniqueLeaseIds.length > 0) {
          const leaseDetailsPromises = uniqueLeaseIds.map(leaseId => 
            fetch(`${BaseUrl}api/lease/getLeaseDetails/${leaseId}`, requestOptions)
              .then(res => res.json())
              .then(result => result.success ? { leaseId, data: result.data } : null)
              .catch(() => null)
          );

          const leaseDetailsResults = await Promise.all(leaseDetailsPromises);
          const leaseMapResult = {};
          leaseDetailsResults.forEach(result => {
            if (result) {
              leaseMapResult[result.leaseId] = result.data;
            }
          });
          setLeaseMap(leaseMapResult);
        }
      } else {
        setTransactions([]);
        setLeaseMap({});
        setPagination({
          total: 0,
          currentPage: 1,
          totalPages: 0,
          limit: itemsPerPage,
        });
      }
    } catch (error) {
      console.error("Error fetching property transactions:", error);
      setTransactions([]);
      setLeaseMap({});
      setPagination({
        total: 0,
        currentPage: 1,
        totalPages: 0,
        limit: itemsPerPage,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLandlordTransactions = async (page = 1) => {
    if (!landlordId) return;

    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    try {
      const transactionsResponse = await fetch(
        `${BaseUrl}api/payment-transaction/landlord/${landlordId}?page=${page}&limit=${itemsPerPage}`,
        requestOptions
      );
      const transactionsResult = await transactionsResponse.json();

      if (transactionsResult.status === "success" && transactionsResult.data) {
        setTransactions(transactionsResult.data);
        if (transactionsResult.pagination) {
          setPagination(transactionsResult.pagination);
        }

        // Get unique lease IDs from transactions
        const uniqueLeaseIds = [...new Set(transactionsResult.data.map(t => t.leaseId).filter(Boolean))];

        // Fetch lease details for tenant info
        if (uniqueLeaseIds.length > 0) {
          const leaseDetailsPromises = uniqueLeaseIds.map(leaseId => 
            fetch(`${BaseUrl}api/lease/getLeaseDetails/${leaseId}`, requestOptions)
              .then(res => res.json())
              .then(result => result.success ? { leaseId, data: result.data } : null)
              .catch(() => null)
          );

          const leaseDetailsResults = await Promise.all(leaseDetailsPromises);
          const leaseMapResult = {};
          leaseDetailsResults.forEach(result => {
            if (result) {
              leaseMapResult[result.leaseId] = result.data;
            }
          });
          setLeaseMap(leaseMapResult);
        }
      } else {
        setTransactions([]);
        setLeaseMap({});
        setPagination({
          total: 0,
          currentPage: 1,
          totalPages: 0,
          limit: itemsPerPage,
        });
      }
    } catch (error) {
      console.error("Error fetching landlord transactions:", error);
      setTransactions([]);
      setLeaseMap({});
      setPagination({
        total: 0,
        currentPage: 1,
        totalPages: 0,
        limit: itemsPerPage,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leaseId) {
      fetchLeaseTransactions(1);
    } else if (propertyId) {
      fetchPropertyTransactions(1);
    } else if (landlordId) {
      fetchLandlordTransactions(1);
    }
  }, [leaseId, propertyId, landlordId]);

  // Get landlord name for breadcrumb
  const getLandlordName = () => {
    if (landlordId && loginSuccess?.user) {
      return `${loginSuccess.user.firstName || ""} ${loginSuccess.user.lastName || ""}`.trim();
    }
    return "";
  };

  return (
    <div className="mt-5 w-full">
      <Breadcrumbs
        classes={`text-darkColor font-popinsSemiBold text-xl`}
        subheading="Transactions"
        onBack={onBack}
        recordId={leaseId ? `LES-${leaseId?.slice(0, 10)}` : (propertyId ? `PROP-${propertyId?.slice(1, 10)}` : (landlordId ? getLandlordName() : ""))}
      />

      <div className="bg-white w-full rounded-lg mt-5">
        <div className="w-[95%] mx-auto h-full py-5">
          <TransactionsList
            transactions={transactions}
            loading={loading}
            leaseDetails={leaseDetails}
            leaseDetailsMap={leaseMap}
            limit={null}
            showHeaders={true}
            showExtendedColumns={true}
          />
          
          {pagination.totalPages > 1 && (
            <div className="mt-5">
              <ReactPaginate
                previousLabel={"Previous"}
                nextLabel={"Next"}
                breakLabel={"..."}
                pageCount={pagination.totalPages}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                forcePage={pagination.currentPage - 1}
                containerClassName={"pagination flex justify-center items-center gap-2"}
                pageClassName={"px-3 py-1 rounded border border-gray-300 cursor-pointer"}
                pageLinkClassName={"text-darkColor font-popinsMedium text-sm"}
                activeClassName={"bg-themeColor text-white border-themeColor"}
                previousClassName={"px-3 py-1 rounded border border-gray-300 cursor-pointer"}
                nextClassName={"px-3 py-1 rounded border border-gray-300 cursor-pointer"}
                disabledClassName={"opacity-50 cursor-not-allowed"}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionsTable;

